import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../api/client';
import { useSettings } from '../../context/SettingsContext';
import { useUI } from '../../context/UIContext';
import { money } from '../../utils/format';
import { IconClose, IconSearch } from '../common/Icons';
import Image from '../common/Image';

export default function SearchOverlay() {
  const { settings } = useSettings();
  const { searchOpen, setSearchOpen } = useUI();
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const input = useRef(null);
  const navigate = useNavigate();
  const symbol = settings.shop?.currencySymbol || '$';

  useEffect(() => {
    if (searchOpen) setTimeout(() => input.current && input.current.focus(), 50);
    else {
      setQ('');
      setItems([]);
    }
  }, [searchOpen]);

  useEffect(() => {
    if (!q.trim()) {
      setItems([]);
      return undefined;
    }
    const t = setTimeout(() => {
      setLoading(true);
      api.get('/products/suggest', { params: { q } }).then((r) => setItems(r.items)).catch(() => {}).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
    setSearchOpen(false);
  };

  if (!searchOpen) return null;
  return (
    <div className="fixed z-[100] overflow-x-hidden h-full overflow-y-scroll inset-0 transition-opacity duration-300 eg-search-overlay" onClick={() => setSearchOpen(false)}>
      <div className="search-container py-4 md:py-8 bg-white relative z-10 min-h-full md:min-h-0" onClick={(e) => e.stopPropagation()}>
        <div className="container-fluid">
          <div className="flex justify-between items-center md:hidden mb-2">
            <h3 className="text-base font-medium">Search our store</h3>
            <button type="button" className="text-black p-2" onClick={() => setSearchOpen(false)} aria-label="Close"><IconClose className="w-5 h-5" /></button>
          </div>
          <div className="flex items-center">
            <div className="sf-logo px-4 w-1/6 justify-center has-logo-img hidden md:flex">
              <Link to="/" className="block py-2.5 logo-img relative"><img src={settings.general?.logo ? `${process.env.REACT_APP_API_URL || ''}${settings.general.logo}` : '/logo.png'} alt={settings.general?.siteName} className="inline-block" style={{ width: 150 }} /></Link>
            </div>
            <div className="w-full md:w-2/3 flex justify-center items-center">
              <form className="relative w-full md:mx-28 flex border border-black rounded-md" onSubmit={submit} role="search">
                <input ref={input} value={q} onChange={(e) => setQ(e.target.value)} type="search" placeholder="Search products..." className="w-full py-2.5 px-4 rounded-md outline-none" aria-label="Search" />
                <button type="submit" className="absolute top-px right-0 py-3 px-3.5" aria-label="Submit search"><IconSearch className="w-5 h-5" /></button>
              </form>
            </div>
            <div className="w-1/6 hidden md:flex justify-end items-center">
              <button type="button" className="p-2" onClick={() => setSearchOpen(false)} aria-label="Close"><IconClose className="w-5 h-5" /></button>
            </div>
          </div>
          {(settings.header?.popularSearches || []).length > 0 && (
            <div className="flex md:justify-center mt-3 flex-wrap">
              <span className="text-[#666] mr-4">Popular Searches:</span>
              <div className="flex items-center flex-wrap">
                {settings.header.popularSearches.map((s) => (
                  <Link key={s} to={`/search?q=${encodeURIComponent(s)}`} className="cursor-pointer underline mr-4 hover:text-gray-800 whitespace-nowrap" onClick={() => setSearchOpen(false)}>{s}</Link>
                ))}
              </div>
            </div>
          )}
          {q.trim() && (
            <div className="mt-6">
              <div className="text-2xl text-[#999] mt-4 md:mt-9 mb-2 md:mb-7 text-center">
                {loading ? 'Searching…' : items.length ? <span>Results for <span className="text-black">“{q}”</span></span> : 'No products found'}
              </div>
              <div className="grid gap-x-3 md:gap-x-6 gap-y-6 grid-cols-2 md:grid-cols-4 lg:grid-cols-5">
                {items.map((p) => (
                  <Link key={p._id} to={`/products/${p.slug}`} className="block text-center" onClick={() => setSearchOpen(false)}>
                    <Image src={p.images?.[0]?.url} alt={p.title} ratio={1} className="mb-3" />
                    <h3 className="text-base font-medium">{p.title}</h3>
                    <p className="text-sm">
                      {p.compareAtPrice > p.price && <span className="line-through text-color-secondary mr-2">{money(p.compareAtPrice, symbol)}</span>}
                      {money(p.price, symbol)}
                    </p>
                  </Link>
                ))}
              </div>
              {items.length > 0 && (
                <div className="flex justify-center mt-10">
                  <Link to={`/search?q=${encodeURIComponent(q)}`} className="sf__btn sf__btn-primary" onClick={() => setSearchOpen(false)}>More Results</Link>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
